/**
 * Ежегодное обслуживание вложений и займов (план 2b, Task 4;
 * interactives.md E8–E10). Мини-игры раздела «деньги» лишь ставят флаги
 * жизни исходом `MinigameOutcome`; движок раз в год (`serviceHoldings`,
 * шаг 1a' тика — до мини-игры года, чтобы покупка не росла в тот же тик)
 * доводит их до результата:
 *
 *  - пирамида (`pyramidInvested`, `pyramidYears`): годы 1 и 2 — выплата
 *    PYRAMID_YIELD от вложенного; год 3 — с p PYRAMID_CRASH_P крах
 *    (вложенное не возвращается, тяга +10, счастье −10), иначе выход:
 *    вложенное возвращается вместе с третьей выплатой;
 *  - монета (`cryptoHeld`): стоимость × логнормальный множитель с ожиданием
 *    CRYPTO_MEAN и σ CRYPTO_SIGMA в год; акции (`stocksHeld`) — то же с
 *    STOCKS_MEAN и STOCKS_SIGMA; записей в журнал нет (курс — только по тапу);
 *  - заём (`loanDue`, `loanKind`): через год возвращается из денег; нехватка
 *    уходит в `debt` с флагом `loanDefaulted`, и пока долг > 0 — с p
 *    LOAN_COLLECTORS_P в год запись `loan.collectors` (счастье −5, отношения −3).
 *
 * Деньги и долг ≥ 0, состояния в [0, 100]; вход не мутируется.
 */
import { clampStats } from './focus';
import { FLAG_URGE, urgeOf } from './minigames';
export { FLAG_LAST_LOSS } from './minigames';
import type { Rng } from './rng';
import type { Life, LogEntry } from './types';

/** Пирамида уже предлагалась (раз в жизнь). */
export const FLAG_PYRAMID_OFFERED = 'pyramidOffered';
/** Вложенная в пирамиду сумма. */
export const FLAG_PYRAMID_INVESTED = 'pyramidInvested';
/** Сколько лет прошло с вложения (0 в год вложения). */
export const FLAG_PYRAMID_YEARS = 'pyramidYears';
/** Текущая стоимость позиции в монете. */
export const FLAG_CRYPTO_HELD = 'cryptoHeld';
/** Текущая стоимость позиции в акциях. */
export const FLAG_STOCKS_HELD = 'stocksHeld';
/** Сумма к возврату по займу через год. */
export const FLAG_LOAN_DUE = 'loanDue';
/** Вид займа: 'pawn' | 'microloan'. */
export const FLAG_LOAN_KIND = 'loanKind';
/** Заём не возвращён: долг под коллекторами. */
export const FLAG_LOAN_DEFAULTED = 'loanDefaulted';

export const PYRAMID_YIELD = 0.4;
export const PYRAMID_PAYOUT_YEARS = 2;
export const PYRAMID_CRASH_P = 0.85;
export const PYRAMID_CRASH_URGE = 10;
export const PYRAMID_CRASH_HAPPINESS = -10;
export const PYRAMID_PAYOUT_KEY = 'pyramid.payout';
export const PYRAMID_CRASH_KEY = 'pyramid.crash';
export const PYRAMID_EXIT_KEY = 'pyramid.exit';

/** Ожидаемый множитель стоимости за год и σ логарифма доходности. */
export const CRYPTO_MEAN = 1.03;
export const CRYPTO_SIGMA = 0.8;
export const STOCKS_MEAN = 1.05;
export const STOCKS_SIGMA = 0.18;

export const LOAN_COLLECTORS_P = 0.3;
export const LOAN_COLLECTORS_HAPPINESS = -5;
export const LOAN_COLLECTORS_RELATIONS = -3;
export const LOAN_REPAID_KEY = 'loan.repaid';
export const LOAN_DEFAULT_KEY = 'loan.default';
export const LOAN_COLLECTORS_KEY = 'loan.collectors';

function numberFlag(life: Life, key: string): number {
  const raw = life.flags[key];
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : 0;
}

function withoutFlags(flags: Life['flags'], keys: readonly string[]): Life['flags'] {
  const next: Life['flags'] = {};
  for (const [k, v] of Object.entries(flags)) if (!keys.includes(k)) next[k] = v;
  return next;
}

/** Логнормальный годовой множитель с заданным ожиданием: mu = ln(mean) − σ²/2. */
export function growthFactor(rng: Rng, mean: number, sigma: number): number {
  return rng.logNormal(Math.log(mean) - (sigma * sigma) / 2, sigma);
}

function servicePyramid(life: Life, rng: Rng, entries: LogEntry[]): Life {
  const invested = numberFlag(life, FLAG_PYRAMID_INVESTED);
  if (invested <= 0) return life;
  const years = numberFlag(life, FLAG_PYRAMID_YEARS) + 1;
  const payout = invested * PYRAMID_YIELD;
  if (years <= PYRAMID_PAYOUT_YEARS) {
    entries.push({ age: life.age, key: PYRAMID_PAYOUT_KEY, params: { amount: Math.round(payout) } });
    return { ...life, money: life.money + payout, flags: { ...life.flags, [FLAG_PYRAMID_YEARS]: years } };
  }
  const flags = { ...withoutFlags(life.flags, [FLAG_PYRAMID_INVESTED, FLAG_PYRAMID_YEARS]), [FLAG_PYRAMID_OFFERED]: true };
  if (rng.chance(PYRAMID_CRASH_P)) {
    entries.push({ age: life.age, key: PYRAMID_CRASH_KEY, params: { amount: Math.round(invested) } });
    const urge = Math.min(100, urgeOf(life) + PYRAMID_CRASH_URGE);
    return {
      ...life,
      stats: clampStats({ ...life.stats, happiness: life.stats.happiness + PYRAMID_CRASH_HAPPINESS }),
      flags: { ...flags, [FLAG_URGE]: urge },
    };
  }
  const back = invested + payout;
  entries.push({ age: life.age, key: PYRAMID_EXIT_KEY, params: { amount: Math.round(back) } });
  return { ...life, money: life.money + back, flags };
}

function growPosition(life: Life, key: string, mean: number, sigma: number, rng: Rng): Life {
  const held = numberFlag(life, key);
  if (held <= 0) return life;
  return { ...life, flags: { ...life.flags, [key]: held * growthFactor(rng, mean, sigma) } };
}

function serviceLoan(life: Life, rng: Rng, entries: LogEntry[]): Life {
  let next = life;
  const due = numberFlag(next, FLAG_LOAN_DUE);
  if (due > 0) {
    const cash = Math.max(0, next.money);
    const paid = Math.min(cash, due);
    const shortfall = due - paid;
    const flags = withoutFlags(next.flags, [FLAG_LOAN_DUE, FLAG_LOAN_KIND]);
    if (shortfall > 0) {
      entries.push({ age: life.age, key: LOAN_DEFAULT_KEY, params: { amount: Math.round(shortfall) } });
      next = { ...next, money: cash - paid, debt: Math.max(0, next.debt) + shortfall, flags: { ...flags, [FLAG_LOAN_DEFAULTED]: true } };
    } else {
      entries.push({ age: life.age, key: LOAN_REPAID_KEY, params: { amount: Math.round(due) } });
      next = { ...next, money: cash - paid, flags };
    }
  }
  if (next.flags[FLAG_LOAN_DEFAULTED] !== true) return next;
  if (!(next.debt > 0)) return { ...next, flags: withoutFlags(next.flags, [FLAG_LOAN_DEFAULTED]) };
  if (!rng.chance(LOAN_COLLECTORS_P)) return next;
  entries.push({ age: life.age, key: LOAN_COLLECTORS_KEY });
  return {
    ...next,
    stats: clampStats({
      ...next.stats,
      happiness: next.stats.happiness + LOAN_COLLECTORS_HAPPINESS,
      relations: next.stats.relations + LOAN_COLLECTORS_RELATIONS,
    }),
  };
}

/** Годовое обслуживание пирамиды, монеты, акций и займа. Жребии — из `rng.fork` по подсистемам. */
export function serviceHoldings(life: Life, rng: Rng): { life: Life; entries: LogEntry[] } {
  const entries: LogEntry[] = [];
  let next = servicePyramid(life, rng.fork('pyramid'), entries);
  next = growPosition(next, FLAG_CRYPTO_HELD, CRYPTO_MEAN, CRYPTO_SIGMA, rng.fork('crypto'));
  next = growPosition(next, FLAG_STOCKS_HELD, STOCKS_MEAN, STOCKS_SIGMA, rng.fork('stocks'));
  next = serviceLoan(next, rng.fork('loan'), entries);
  return { life: next, entries };
}
